// Form rules for a plan and its versions.
// They mirror data/schemas/plan.js in mastermind_core.

export const NAME_MAX = 160;

// THE STATES OF A VERSION, and what can be done in each.
//
//    draft  ──submit──▶  in_review  ──approve──▶  approved
//      ▲                      │                       │
//      └──request-changes─────┘                       │
//                                                     │
//      next version (draft) ◀───── POST /versions ────┘
export const VERSION_STATE_LABEL = {
    draft:     'Borrador',
    in_review: 'En revisión',
    approved:  'Aprobada',
};

export const VERSION_STATE_HINT = {
    draft:     'Se edita. Guardar no le cambia el número.',
    in_review: 'Congelada: no se edita. Se devuelve con razón, o se aprueba.',
    approved:  'Fija. No se edita ni se revoca: corregirla es preparar la siguiente.',
};

// CERO REPOSITORIOS ES EL PROYECTO ENTERO.
//
// It is the rule the backend lost twice (T10.8, T10.9) and wrote into its own
// CLAUDE.md afterwards: a scope filter reads "the ones that reach this", never
// "the ones that say exactly this". An empty `repository_ids` is the widest
// scope there is, not the narrowest.
//
// So the form asks the question out loud instead of leaving an empty list to be
// interpreted. Somebody who wants the whole project picks it; somebody who
// leaves the repository box empty by accident is not silently granted
// everything.
export const SCOPE_ALL = 'all';
export const SCOPE_SOME = 'some';

export const SCOPE_OPTIONS = [
    {
        value: SCOPE_ALL,
        label: 'Todo el proyecto',
        hint: 'La versión rige sobre cualquier repositorio del proyecto, incluidos los que se agreguen después.',
    },
    {
        value: SCOPE_SOME,
        label: 'Repositorios específicos',
        hint: 'La versión rige solo sobre los que elijas.',
    },
];

// Reading a plan LIST row back. `[]` means the whole project. A single version
// is not read with this: utils/version.js reads its `scope` block and refuses
// to treat a missing one as the whole project.
export function scopeOf(repositoryIds) {
    return Array.isArray(repositoryIds) && repositoryIds.length > 0 ? SCOPE_SOME : SCOPE_ALL;
}

export function scopeLabel(repositoryIds, names) {
    if (scopeOf(repositoryIds) === SCOPE_ALL) return 'Todo el proyecto';
    if (names?.length) return names.join(' · ');
    return `${repositoryIds.length} repositorio${repositoryIds.length === 1 ? '' : 's'}`;
}

export const EMPTY_PLAN = {
    project_id: '',
    name: '',
    content: '',
    scope: SCOPE_ALL,
    repository_ids: [],
};

export function validatePlan(values, { withProject }) {
    const errors = {};
    const name = values.name.trim();

    if (withProject && !values.project_id) errors.project_id = 'El proyecto es requerido';

    if (!name) errors.name = 'El nombre del plan es requerido';
    else if (name.length > NAME_MAX) errors.name = `No puede pasar de ${NAME_MAX} caracteres`;

    if (!values.content.trim()) errors.content = 'El contenido es requerido';

    if (values.scope === SCOPE_SOME && values.repository_ids.length === 0) {
        errors.repository_ids = 'Elegí al menos un repositorio, o cambiá el alcance a todo el proyecto';
    }

    return errors;
}

export function toCreatePlanPayload(values) {
    return {
        project_id: Number(values.project_id),
        name: values.name.trim(),
        content: values.content,
        // Explicit both ways: `[]` says "the whole project" on purpose, and is
        // not the accident of an unfilled field.
        repository_ids: values.scope === SCOPE_SOME
            ? values.repository_ids.map(Number)
            : [],
    };
}

// Saving a draft lives in utils/version.js, next to the adapter of the
// version contract: the payload is diffed against what was READ, and only that
// file knows how a version is read.

// The optional template. These are SECTIONS OF THE CONTENT, not new columns:
// nothing in `plans` or `plan_versions` knows about them, and the day somebody
// wants a different shape they change the text and no migration happens.
export const PLAN_TEMPLATE = `# Propósito

Qué problema resuelve y para quién.

# Alcance

Qué entra. Y, más importante, qué NO entra.

# Comportamiento esperado

Qué tiene que poder hacer alguien cuando esto exista.

# Criterios

Cómo se sabe que está hecho. Uno por línea, comprobable.

# Preguntas pendientes

Lo que todavía no está decidido. Una versión se puede aprobar con preguntas
abiertas; un tier no se puede acordar con dudas.
`;
